import React from "react";
import { useParams, Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { shopApi } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { useCart } from "@/lib/store";
import { toast } from "sonner";
import { Package, ArrowRight, Tag } from "lucide-react";

export default function Bundle() {
  const { slug } = useParams();
  const { data: bundle, isLoading, error } = useQuery({ queryKey: ["bundle", slug], queryFn: () => shopApi.bundle(slug), retry: false });
  const { add } = useCart();

  if (isLoading) return <div className="p-16 text-center text-muted-foreground">Loading…</div>;
  if (error || !bundle) return <div className="p-16 text-center">Bundle not found.</div>;

  const price = bundle.bundle_price ?? (bundle.msrp_total * (1 - (bundle.discount_percent || 0) / 100));
  const savings = bundle.msrp_total - price;
  const savingsPct = Math.round((savings / bundle.msrp_total) * 100);

  const addAll = () => {
    for (const p of bundle.products) add(p, 1, null, bundle.bundle_id, false);
    toast.success("Bundle added to cart");
  };

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
      <nav className="text-xs text-muted-foreground mb-6 flex items-center gap-2">
        <Link to="/">Home</Link> / <Link to="/shop">Shop</Link> / <span className="text-white">{bundle.title}</span>
      </nav>

      <div className="grid md:grid-cols-2 gap-10 items-start">
        <div>
          <div className="aspect-video rounded-2xl bg-card border border-white/5 overflow-hidden relative">
            {bundle.hero_image ? (
              <img src={bundle.hero_image} alt={bundle.title} className="w-full h-full object-cover" />
            ) : (
              <div className="w-full h-full grid grid-cols-2 gap-1">
                {bundle.products.slice(0, 4).map((p) => <img key={p.product_id} src={p.images?.[0]} alt="" className="w-full h-full object-cover" />)}
              </div>
            )}
            <span className="absolute top-4 left-4 chip bg-primary text-primary-foreground border-transparent font-bold">-{savingsPct}%</span>
          </div>
        </div>
        <div>
          <span className="chip mb-3"><Package className="h-3 w-3 text-primary" /> Bundle deal</span>
          <h1 className="font-display text-3xl md:text-4xl font-bold tracking-tight mb-3">{bundle.title}</h1>
          <p className="text-muted-foreground mb-6">Handpicked combo — save when you buy them together.</p>
          <div className="flex items-baseline gap-3 mb-6">
            <span className="text-4xl font-bold" data-testid="bundle-price">${price.toFixed(2)}</span>
            <span className="text-lg text-muted-foreground line-through">${bundle.msrp_total.toFixed(2)}</span>
            <span className="chip text-primary border-primary/30 flex items-center gap-1"><Tag className="h-3 w-3" /> Save ${savings.toFixed(2)}</span>
          </div>

          <div className="rounded-2xl bg-card border border-white/5 p-4 mb-6">
            <p className="text-xs uppercase tracking-widest text-muted-foreground mb-3">What's inside</p>
            <div className="space-y-2">
              {bundle.products.map((p) => (
                <Link key={p.product_id} to={`/product/${p.slug}`} className="flex items-center gap-3 hover:bg-secondary/50 rounded-lg p-2 -mx-2 transition-colors" data-testid={`bundle-item-${p.product_id}`}>
                  <img src={p.images?.[0]} className="h-12 w-12 rounded-lg object-cover" alt="" />
                  <div className="flex-1"><p className="text-sm">{p.title}</p><p className="text-xs text-muted-foreground">${p.price.toFixed(2)}</p></div>
                  <ArrowRight className="h-4 w-4 text-muted-foreground" />
                </Link>
              ))}
            </div>
          </div>

          <Button className="btn-primary w-full h-12" onClick={addAll} data-testid="add-bundle-to-cart">Add bundle to cart · ${price.toFixed(2)}</Button>
        </div>
      </div>
    </div>
  );
}
